/**
 * Props.js — every repeated object in BLACKOUT: KHALIJ.
 *
 * Repeated props are authored once and drawn with InstancedMesh (one draw call
 * per prop-kind × material). Unique hero props (the sedan, the pickup) are
 * merged straight into the static batch so they cost no extra draw call.
 *
 * owner: level
 */
import * as THREE from 'three';
import {
  chamferBox, chamferCyl, taperCyl, quadXZ, xform, blade, tube, alignX,
  stripAttrs, extrudeShape,
} from './Geo.js';
import { rebar } from './kit/Detail.js';

const AO = { from: 0, to: 0.55, dark: 0.55 };
const M = (pos, rot = 0, scale = 1) => xform(pos, rot, scale);

/* ------------------------------------------------------------------ *
 * prop definitions
 * ------------------------------------------------------------------ */

/**
 * Shadow policy, per prop kind.
 *
 * A shadow cascade re-draws every caster, so each `cast: true` prop costs one
 * draw call *per cascade* on top of its main-pass draw. A prop earns that only
 * if its cast shadow is legible from standing height: anything ankle-high, flat
 * on the deck, or permanently indoors is `cast: false` and leans on GTAO plus
 * the baked contact darkening instead.
 */
const CASTS = {
  crate_wood: true, crate_wood_lg: true, crate_plastic: true, crate_metal: true,
  barrel: true, barrel_blue: true, sandbag: true, stall: true, table: true,
  pot_plant: true, olive_tree: true, palm_tree: true, street_lamp: true,
  // ankle-high, flat, or indoor-only — no readable sun shadow
  ammo_box: false, jerrycan: false, gas_bottle: false, tyre: false, pallet: false,
  cinder_block: false, chair: false, produce_crate: false, basket: false,
  rubble_chunk: false, brick_debris: false, grass_tuft: false, bucket: false,
  cardboard: false, road_sign: false, ac_wall_unit: false,
};

export function defineProps(w) {
  const P = w.props;
  const g = (geo, mat, surface, extra = {}) => ({ geo, mat, surface, ao: AO, ...extra });
  /**
   * Define a prop kind. Every part that can share a material *does*, because a
   * prop kind costs one draw call per distinct material — the difference
   * between oak battens and pine boards is a tint, not a second draw.
   */
  const def = (kind, parts) => P.define(kind, parts, { cast: CASTS[kind] !== false });

  /* --- crates ------------------------------------------------------- */
  const woodCrate = (s) => {
    const parts = [];
    const body = chamferBox(s, s * 0.86, s * 0.9, 0.03);
    parts.push(g(body, 'wood_crate', 'wood', { matrix: M([0, s * 0.43, 0]) }));
    // corner battens — same material, sawn-timber tint
    for (const sx of [-1, 1]) for (const sz of [-1, 1]) {
      const b = chamferBox(0.07, s * 0.88, 0.07, 0.017);
      parts.push(g(b, 'wood_crate', 'wood', { matrix: M([sx * (s / 2 - 0.03), s * 0.43, sz * (s * 0.45 - 0.03)]), tint: [0.82, 0.76, 0.68] }));
    }
    for (const y of [0.22, 0.66]) {
      const b = chamferBox(s + 0.02, 0.08, s * 0.92, 0.02);
      parts.push(g(b, 'wood_crate', 'wood', { matrix: M([0, s * y, 0]), tint: [1.08, 1.02, 0.92] }));
    }
    return parts;
  };
  def('crate_wood', woodCrate(0.72));
  /*
   * `crate_wood_lg` used to be a second definition of the same crate at a
   * different size, and a prop kind costs a draw call per material however many
   * instances it has. It is now the same InstancedMesh at 1.458x — same
   * silhouette difference, one fewer draw call, and per-instance non-uniform
   * scale (see `scatter`) makes the two read as different boxes anyway.
   */
  P.alias('crate_wood_lg', 'crate_wood', 1.458);

  def('crate_plastic', [
    g(chamferBox(0.60, 0.36, 0.42, 0.034), 'plastic_crate', 'plastic', { matrix: M([0, 0.18, 0]) }),
    g(chamferBox(0.64, 0.055, 0.46, 0.022), 'plastic_crate', 'plastic', { matrix: M([0, 0.355, 0]), tint: [1.1, 1.08, 1.04] }),
    g(chamferBox(0.10, 0.30, 0.10, 0.02), 'plastic_crate', 'plastic', { matrix: M([0.30, 0.16, 0]) }),
    g(chamferBox(0.10, 0.30, 0.10, 0.02), 'plastic_crate', 'plastic', { matrix: M([-0.30, 0.16, 0]) }),
  ]);

  def('ammo_box', [
    g(chamferBox(0.52, 0.24, 0.28, 0.028), 'metal_painted', 'metal', { matrix: M([0, 0.12, 0]) }),
    g(chamferBox(0.54, 0.055, 0.30, 0.02), 'metal_painted', 'metal', { matrix: M([0, 0.255, 0]), tint: [0.92, 0.94, 0.9] }),
    g(chamferBox(0.16, 0.05, 0.06, 0.012), 'metal_painted', 'metal', { matrix: M([0, 0.30, 0]), tint: [0.78, 0.62, 0.5] }),
    g(chamferBox(0.05, 0.09, 0.04, 0.01), 'metal_painted', 'metal', { matrix: M([0.27, 0.21, 0]), tint: [0.78, 0.62, 0.5] }),
  ]);

  def('crate_metal', [
    g(chamferBox(0.86, 0.52, 0.60, 0.036), 'steel_brushed', 'metal', { matrix: M([0, 0.26, 0]) }),
    g(chamferBox(0.90, 0.065, 0.64, 0.024), 'steel_brushed', 'metal', { matrix: M([0, 0.55, 0]), tint: [1.05, 1.05, 1.06] }),
    ...[-1, 1].map((s) => g(chamferBox(0.06, 0.50, 0.62, 0.016), 'steel_brushed', 'metal', { matrix: M([s * 0.42, 0.26, 0]), tint: [0.7, 0.72, 0.74] })),
  ]);

  /* --- barrels, cans ------------------------------------------------- */
  def('barrel', [
    g(chamferCyl(0.29, 0.88, 16, 0.03), 'metal_rusted', 'metal', { matrix: M([0, 0.44, 0]) }),
    g(chamferCyl(0.305, 0.05, 16, 0.012), 'metal_rusted', 'metal', { matrix: M([0, 0.24, 0]), tint: [1.1, 1.04, 0.96] }),
    g(chamferCyl(0.305, 0.05, 16, 0.012), 'metal_rusted', 'metal', { matrix: M([0, 0.64, 0]), tint: [1.1, 1.04, 0.96] }),
    g(chamferCyl(0.10, 0.03, 8, 0.008), 'metal_rusted', 'metal', { matrix: M([0.13, 0.885, 0]), tint: [0.8, 0.82, 0.84] }),
  ]);
  def('barrel_blue', [
    g(chamferCyl(0.27, 0.90, 14, 0.03), 'plastic_crate', 'plastic', { matrix: M([0, 0.45, 0]) }),
    g(chamferCyl(0.285, 0.045, 14, 0.012), 'plastic_crate', 'plastic', { matrix: M([0, 0.30, 0]), tint: [1.06, 1.06, 1.08] }),
    g(chamferCyl(0.285, 0.045, 14, 0.012), 'plastic_crate', 'plastic', { matrix: M([0, 0.62, 0]), tint: [1.06, 1.06, 1.08] }),
  ]);
  def('jerrycan', [
    g(chamferBox(0.34, 0.44, 0.16, 0.03), 'metal_painted', 'metal', { matrix: M([0, 0.22, 0]) }),
    g(chamferBox(0.28, 0.05, 0.05, 0.012), 'metal_painted', 'metal', { matrix: M([0, 0.46, 0]), tint: [0.88, 0.9, 0.86] }),
    g(taperCyl(0.045, 0.035, 0.10, 8), 'metal_painted', 'metal', { matrix: M([0.11, 0.48, 0]), tint: [0.74, 0.58, 0.46] }),
  ]);
  def('gas_bottle', [
    g(chamferCyl(0.17, 0.58, 12, 0.05), 'metal_painted', 'metal', { matrix: M([0, 0.29, 0]) }),
    g(chamferCyl(0.06, 0.10, 8, 0.02), 'metal_painted', 'metal', { matrix: M([0, 0.62, 0]), tint: [0.72, 0.56, 0.44] }),
    g(chamferCyl(0.13, 0.06, 12, 0.02), 'metal_painted', 'metal', { matrix: M([0, 0.66, 0]), tint: [0.72, 0.56, 0.44] }),
  ]);

  /* --- sandbags, tyres, pallets -------------------------------------- */
  {
    /*
     * Two bag shapes, not one.
     *
     * A wall built from a single instanced bag repeats the same weave phase and
     * the same silhouette every 0.5 m, which is exactly what `level_detail`
     * (1430–1920, 395–470) picked up. Instancing cannot rotate UVs per instance,
     * so the UV phase has to live in the geometry: `sandbag_b` is a different
     * bag — plumper, shorter, seam the other way — with its planar UVs offset
     * half a tile. `sandbagWall` alternates them, and per-instance tint (see
     * below) does the rest. Cost: one draw call, paid for by folding
     * `crate_wood_lg` and `brick_debris` into their neighbours.
     */
    const bagA = chamferBox(0.50, 0.21, 0.31, 0.098);
    def('sandbag', [
      g(bagA, 'sandbag', 'fabric', { matrix: M([0, 0.105, 0]), uvScale: 1.2, tint: [0.62, 0.60, 0.55], ao: { from: -0.12, to: 0.3, dark: 0.68 } }),
    ]);
    const bagB = chamferBox(0.47, 0.235, 0.335, 0.112);
    def('sandbag_b', [
      g(bagB, 'sandbag', 'fabric', {
        matrix: M([0, 0.115, 0], 0.06), tint: [0.66, 0.62, 0.56],
        uvScale: 1.2, uvOffset: [0.41, 0.63],
        ao: { from: -0.12, to: 0.3, dark: 0.68 },
      }),
    ]);
  }
  def('tyre', [
    (() => {
      const t = stripAttrs(new THREE.TorusGeometry(0.30, 0.115, 8, 16).toNonIndexed());
      t.rotateX(Math.PI / 2);
      return g(t, 'rubber', 'rubber', { matrix: M([0, 0.115, 0]) });
    })(),
  ]);
  def('pallet', (() => {
    const parts = [];
    for (let i = 0; i < 5; i++) {
      parts.push(g(chamferBox(1.14, 0.04, 0.13, 0.014), 'wood_plank', 'wood', { matrix: M([0, 0.145, -0.36 + i * 0.18]) }));
    }
    for (const z of [-0.36, 0, 0.36]) {
      parts.push(g(chamferBox(1.14, 0.095, 0.14, 0.02), 'wood_plank', 'wood', { matrix: M([0, 0.06, z]) }));
    }
    for (let i = 0; i < 3; i++) {
      parts.push(g(chamferBox(0.13, 0.04, 0.82, 0.014), 'wood_plank', 'wood', { matrix: M([-0.45 + i * 0.45, 0.015, 0]) }));
    }
    return parts;
  })());

  def('cinder_block', [
    g(chamferBox(0.44, 0.20, 0.20, 0.026), 'concrete_wall', 'concrete', { matrix: M([0, 0.10, 0]), uvScale: 0.3, ao: { from: -0.05, to: 0.18, dark: 0.76 }, tint: [0.94, 0.97, 1.03] }),
  ]);

  /* --- market furniture ---------------------------------------------- */
  def('stall', (() => {
    const parts = [];
    // counter
    parts.push(g(chamferBox(2.4, 0.10, 1.05, 0.03), 'wood_plank', 'wood', { matrix: M([0, 0.94, 0]) }));
    parts.push(g(chamferBox(2.36, 0.55, 0.065, 0.024), 'wood_plank', 'wood', { matrix: M([0, 0.64, 0.49]), tint: [1.12, 1.04, 0.9] }));
    for (const sx of [-1, 1]) for (const sz of [-1, 1]) {
      parts.push(g(chamferBox(0.09, 0.90, 0.09, 0.022), 'wood_plank', 'wood', { matrix: M([sx * 1.12, 0.45, sz * 0.46]) }));
    }
    // shelf
    parts.push(g(chamferBox(2.2, 0.055, 0.85, 0.022), 'wood_plank', 'wood', { matrix: M([0, 0.42, 0]), tint: [1.12, 1.04, 0.9] }));
    // awning posts + rail
    for (const sx of [-1, 1]) {
      parts.push(g(chamferBox(0.07, 2.35, 0.07, 0.012), 'metal_rusted', 'metal', { matrix: M([sx * 1.18, 1.17, -0.48]) }));
      parts.push(g(chamferBox(0.07, 2.05, 0.07, 0.012), 'metal_rusted', 'metal', { matrix: M([sx * 1.18, 1.02, 0.5]) }));
    }
    parts.push(g(chamferBox(2.5, 0.065, 0.065, 0.016), 'metal_rusted', 'metal', { matrix: M([0, 2.33, -0.48]) }));
    parts.push(g(chamferBox(2.5, 0.065, 0.065, 0.016), 'metal_rusted', 'metal', { matrix: M([0, 2.03, 0.5]) }));
    return parts;
  })());

  def('table', [
    g(chamferBox(1.5, 0.065, 0.78, 0.026), 'wood_plank', 'wood', { matrix: M([0, 0.74, 0]) }),
    ...[[-1, -1], [1, -1], [-1, 1], [1, 1]].map(([sx, sz]) =>
      g(chamferBox(0.07, 0.72, 0.07, 0.018), 'wood_plank', 'wood', { matrix: M([sx * 0.68, 0.36, sz * 0.33]) })),
  ]);

  def('chair', [
    g(chamferBox(0.44, 0.05, 0.42, 0.024), 'plastic_crate', 'plastic', { matrix: M([0, 0.44, 0]) }),
    g(chamferBox(0.42, 0.46, 0.055, 0.026), 'plastic_crate', 'plastic', { matrix: M([0, 0.68, -0.19]) }),
    ...[[-1, -1], [1, -1], [-1, 1], [1, 1]].map(([sx, sz]) =>
      g(chamferBox(0.045, 0.44, 0.045, 0.012), 'plastic_crate', 'plastic', { matrix: M([sx * 0.18, 0.22, sz * 0.17]) })),
  ]);

  def('produce_crate', (() => {
    const parts = [];
    parts.push(g(chamferBox(0.56, 0.22, 0.40, 0.028), 'wood_crate', 'wood', { matrix: M([0, 0.11, 0]) }));
    // Heaped produce. One instanced definition serves 43 crates, so the variety
    // has to live *inside* the heap: a market stall piled with identically green
    // spheres is the loudest "prop library" tell in the square.
    const PRODUCE = [
      [1.05, 0.62, 0.22],   // oranges
      [1.15, 0.30, 0.16],   // tomatoes
      [0.62, 0.72, 0.28],   // limes
      [0.92, 0.86, 0.34],   // melons
      [0.48, 0.34, 0.42],   // aubergine
      [0.86, 0.74, 0.52],   // onions
    ];
    const rng = w.rng;
    const hue = PRODUCE[rng.int(0, PRODUCE.length - 1)];
    for (let i = 0; i < 11; i++) {
      const s = rng.range(0.05, 0.09);
      const sph = stripAttrs(new THREE.SphereGeometry(s, 7, 5).toNonIndexed());
      const k = rng.range(0.82, 1.18);
      parts.push(g(sph, 'foliage', 'foliage', { uv: 'keep',
        tint: [hue[0] * k, hue[1] * k, hue[2] * k],
        matrix: xform([rng.range(-0.21, 0.21), 0.215 + rng.range(0, 0.07), rng.range(-0.14, 0.14)],
          [rng.range(-0.6, 0.6), rng.range(0, 3.1), rng.range(-0.6, 0.6)],
          [1, rng.range(0.72, 1.0), 1]),
      }));
    }
    return parts;
  })());

  def('basket', [
    g(taperCyl(0.20, 0.28, 0.30, 12), 'wood_crate', 'wood', { matrix: M([0, 0.15, 0]), tint: [0.9, 0.78, 0.58] }),
    g(chamferCyl(0.29, 0.04, 12, 0.012), 'wood_crate', 'wood', { matrix: M([0, 0.30, 0]), tint: [1.02, 0.9, 0.7] }),
  ]);

  def('pot_plant', (() => {
    const parts = [];
    parts.push(g(taperCyl(0.20, 0.28, 0.42, 12), 'tile_ceramic', 'concrete', { matrix: M([0, 0.21, 0]), tint: [1.25, 0.72, 0.5] }));
    parts.push(g(chamferCyl(0.30, 0.05, 12, 0.015), 'tile_ceramic', 'concrete', { matrix: M([0, 0.42, 0]), tint: [1.2, 0.7, 0.48] }));
    parts.push(g(chamferCyl(0.25, 0.05, 10, 0.01), 'tile_ceramic', 'concrete', { matrix: M([0, 0.42, 0]), tint: [0.42, 0.36, 0.3] }));
    const rng = w.rng;
    for (let i = 0; i < 22; i++) {
      const a = rng.range(0, Math.PI * 2);
      parts.push(g(blade(rng.range(0.4, 0.8), 0.085, 0.55, 4, rng.range(-0.5, 0.5)), 'foliage', 'foliage', { uv: 'keep', tint: [0.55, 0.58, 0.3],
        matrix: xform([Math.cos(a) * 0.09, 0.44, Math.sin(a) * 0.09], [rng.range(-0.45, 0.45), a, 0], 1),
      }));
    }
    return parts;
  })());

  /* --- rubble & debris ------------------------------------------------ */
  /*
   * One rubble definition now carries both the chunky spall and the snapped
   * blockwork that used to be a separate `brick_debris` kind on `brick_wall`.
   *
   * Two reasons. Draw calls: a second kind cost a full InstancedMesh for 46
   * pieces of debris. Art: `level_detail` reported these as "flat cards
   * intersecting the floor at random angles" — they were 6-8 cm slabs tilted
   * +-0.42 rad about X and Z with their pivot ON the ground, so between a third
   * and a half of every piece was buried and what stuck out was a thin wedge.
   * The pieces are chunkier now, the tilt is halved, and each sits ON its own
   * half-height so it rests on the surface instead of slicing through it.
   */
  def('rubble_chunk', (() => {
    const rng = w.rng;
    const parts = [];
    for (let i = 0; i < 9; i++) {
      const s = rng.range(0.09, 0.24);
      // some chunks keep a painted face, most are broken core — the value
      // spread is what stops a pile reading as one extruded shape
      const v = rng() < 0.3 ? rng.range(0.88, 1.06) : rng.range(0.62, 0.86);
      const brick = i >= 5;                      // the snapped-blockwork half
      const hx = brick ? rng.range(0.09, 0.13) : s;
      const hy = brick ? rng.range(0.055, 0.085) : s * rng.range(0.45, 0.9);
      const hz = brick ? rng.range(0.05, 0.075) : s * rng.range(0.6, 1.3);
      parts.push(g(chamferBox(hx, hy, hz, 0.026), 'concrete_wall', 'concrete', {
        // 0.18, not the 1.2 default: a 20 cm chunk at uvScale 1.2 samples 17 % of one
        // texture tile, so whatever colour happens to sit there fills the whole
        // piece. That is why the rubble read as rusted chocolate beside a grey kerb.
        uvScale: 0.18, ao: { from: -0.06, to: 0.15, dark: 0.80 },
        tint: [v * 0.92, v, v * 1.08],
        // pivot at hy/2 so the piece rests on y = 0 rather than half in it
        matrix: xform([rng.range(-0.24, 0.24), hy * 0.52, rng.range(-0.24, 0.24)],
          [rng.range(-0.22, 0.22), rng.range(0, 3.1), rng.range(-0.22, 0.22)], 1),
      }));
    }
    return parts;
  })());
  /*
   * Dusty snapped blockwork. Placed through the same InstancedMesh at 0.72x with
   * a buff tint — a vertex tint can only scale channels, so the buff has to come
   * from a lower, cooler multiplier rather than from `brick_wall`'s saturated
   * red, which no tint can desaturate. (Walls.js makes the same argument for the
   * exposed core under blown plaster.)
   */
  P.alias('brick_debris', 'rubble_chunk', 0.72, [0.96, 0.99, 1.02]);

  /* --- foliage --------------------------------------------------------- */
  def('grass_tuft', (() => {
    const rng = w.rng;
    const parts = [];
    for (let i = 0; i < 11; i++) {
      const a = rng.range(0, Math.PI * 2);
      const dry = rng.range(0.55, 0.95);
      parts.push(g(blade(rng.range(0.22, 0.46), 0.016, 0.34, 3, rng.range(-0.3, 0.3)), 'foliage', 'foliage', { uv: 'keep',
        matrix: xform([Math.cos(a) * 0.045, 0, Math.sin(a) * 0.045], [rng.range(-0.34, 0.34), a, 0], 1),
        tint: [0.46 * dry, 0.44 * dry, 0.20 * dry],
        ao: null,
      }));
    }
    return parts;
  })());

  def('olive_tree', (() => {
    const rng = w.rng;
    const parts = [];
    const trunkH = 1.7;
    parts.push(g(taperCyl(0.20, 0.13, trunkH, 9), 'wood_plank', 'wood', { matrix: M([0, trunkH / 2, 0]) }));
    // gnarled base flare
    for (let i = 0; i < 4; i++) {
      const a = rng.range(0, Math.PI * 2);
      parts.push(g(taperCyl(0.10, 0.05, 0.55, 6), 'wood_plank', 'wood', {
        matrix: xform([Math.cos(a) * 0.12, 0.26, Math.sin(a) * 0.12], [rng.range(0.15, 0.4), a, 0], 1),
      }));
    }
    // limbs
    const limbs = [];
    for (let i = 0; i < 5; i++) {
      const a = (i / 5) * Math.PI * 2 + rng.range(-0.3, 0.3);
      const len = rng.range(0.9, 1.5);
      const tip = [Math.cos(a) * len, trunkH + rng.range(0.5, 1.1), Math.sin(a) * len];
      limbs.push(tip);
      parts.push(g(tube([[0, trunkH - 0.15, 0], [Math.cos(a) * len * 0.5, trunkH + 0.35, Math.sin(a) * len * 0.5], tip], 0.055, 5), 'wood_plank', 'wood', {}));
    }
    // canopy: leaf clusters at the limb tips
    for (const tip of limbs) {
      for (let k = 0; k < 14; k++) {
        const a = rng.range(0, Math.PI * 2);
        const rr = rng.range(0, 0.75);
        parts.push(g(blade(rng.range(0.35, 0.6), rng.range(0.07, 0.12), 0.5, 3, rng.range(-0.7, 0.7)), 'foliage', 'foliage', { uv: 'keep',
          matrix: xform([tip[0] + Math.cos(a) * rr, tip[1] + rng.range(-0.35, 0.5), tip[2] + Math.sin(a) * rr],
            [rng.range(-1.3, 0.6), a, rng.range(-0.5, 0.5)], 1),
          ao: null,
        }));
      }
    }
    return parts;
  })());

  def('palm_tree', (() => {
    const rng = w.rng;
    const parts = [];
    const h = 5.2;
    const segs = 9;
    for (let i = 0; i < segs; i++) {
      const t = i / segs;
      const y = h * t;
      const lean = Math.sin(t * 1.4) * 0.45;
      parts.push(g(taperCyl(0.24 - t * 0.11, 0.22 - t * 0.11, h / segs + 0.03, 8), 'wood_plank', 'wood', {
        matrix: xform([lean, y + h / segs / 2, lean * 0.3], [0, i * 0.7, 0], 1),
      }));
    }
    const top = [Math.sin(1.4) * 0.45, h, Math.sin(1.4) * 0.135];
    for (let i = 0; i < 11; i++) {
      const a = (i / 11) * Math.PI * 2 + rng.range(-0.15, 0.15);
      parts.push(g(blade(rng.range(1.7, 2.4), 0.20, 0.85, 6, 0), 'foliage', 'foliage', { uv: 'keep',
        matrix: xform([top[0], top[1], top[2]], [rng.range(0.9, 1.5), a, 0], 1), ao: null,
      }));
    }
    for (let i = 0; i < 5; i++) {
      const a = rng.range(0, Math.PI * 2);
      parts.push(g(blade(rng.range(1.0, 1.6), 0.16, 1.1, 5, 0), 'foliage', 'foliage', { uv: 'keep',
        matrix: xform([top[0], top[1] - 0.15, top[2]], [rng.range(1.9, 2.5), a, 0], 1),
        tint: [0.78, 0.72, 0.55], ao: null,
      }));
    }
    return parts;
  })());

  /* --- street furniture ------------------------------------------------ */
  def('street_lamp', (() => {
    const parts = [];
    parts.push(g(chamferCyl(0.13, 0.35, 10, 0.03), 'metal_painted', 'metal', { matrix: M([0, 0.17, 0]), tint: [0.72, 0.71, 0.68] }));
    parts.push(g(taperCyl(0.095, 0.06, 4.6, 9), 'metal_painted', 'metal', { matrix: M([0, 2.5, 0]) }));
    parts.push(g(tube([[0, 4.6, 0], [0, 5.0, 0.25], [0, 5.05, 0.85]], 0.055, 6), 'metal_painted', 'metal', {}));
    parts.push(g(chamferBox(0.30, 0.13, 0.62, 0.03), 'metal_painted', 'metal', { matrix: M([0, 5.0, 1.15]) }));
    parts.push(g(chamferBox(0.24, 0.05, 0.50, 0.02), 'glass', 'glass', { matrix: M([0, 4.92, 1.15]) }));
    return parts;
  })());

  def('road_sign', [
    g(chamferCyl(0.045, 2.5, 8, 0.012), 'metal_painted', 'metal', { matrix: M([0, 1.25, 0]) }),
    g(chamferBox(0.62, 0.62, 0.045, 0.022), 'metal_painted', 'metal', { matrix: M([0, 2.25, 0.03]) }),
    g(chamferBox(0.52, 0.52, 0.02, 0.015), 'metal_painted', 'metal', { matrix: M([0, 2.25, 0.055]), tint: [0.78, 0.6, 0.46] }),
  ]);

  def('ac_wall_unit', [
    g(chamferBox(0.78, 0.55, 0.36, 0.032), 'metal_painted', 'metal', { matrix: M([0, 0, 0]) }),
    g(chamferCyl(0.20, 0.05, 12, 0.012), 'metal_grate', 'metal', { matrix: xform([0, 0, 0.19], [Math.PI / 2, 0, 0], 1) }),
    ...[-1, 1].map((s) => g(chamferBox(0.06, 0.10, 0.44, 0.01), 'metal_painted', 'metal', { matrix: M([s * 0.3, -0.31, 0]), tint: [0.74, 0.58, 0.46] })),
  ]);

  def('bucket', [
    g(taperCyl(0.13, 0.17, 0.30, 10), 'plastic_crate', 'plastic', { matrix: M([0, 0.15, 0]) }),
    g(chamferCyl(0.175, 0.03, 10, 0.008), 'plastic_crate', 'plastic', { matrix: M([0, 0.30, 0]) }),
  ]);

  def('cardboard', [
    g(chamferBox(0.46, 0.34, 0.36, 0.028), 'plywood', 'wood', { matrix: M([0, 0.17, 0]) }),
    g(chamferBox(0.48, 0.03, 0.20, 0.01), 'plywood', 'wood', { matrix: M([0, 0.35, 0.09]) }),
  ]);

  return P;
}

/* ------------------------------------------------------------------ *
 * per-instance variation
 * ------------------------------------------------------------------ */

/*
 * A tint multiplies the material's own albedo, so from `plastic_crate`'s
 * saturated blue (~0.24/0.42/0.64 linear) a per-channel scale genuinely reaches
 * bleached white, faded red, green and yellow — which is why every plastic crate,
 * chair, bucket and drum in the map no longer has to be the same blue. The blue
 * chair in `level_interior` was read by the reviewer as "an untextured emissive
 * blue rectangle"; that is what one saturated hue on an unlit prop looks like.
 */
const PLASTIC_TINT = [
  [1.00, 1.00, 1.00], [2.55, 1.55, 1.00], [3.05, 1.02, 0.56],
  [1.05, 1.78, 0.72], [2.70, 2.05, 0.90], [1.60, 1.35, 1.05],
];

/*
 * Painted metal. `level_overview` was marked "the entire frame is one sepia hue
 * — automatic fail", and the props are half of why: every drum, can and bottle
 * in the map was a value jitter on one warm albedo.
 *
 * The reference is not a rainbow. `refs/mw3_04.jpg` is a fundamentally warm
 * frame that carries exactly two strong non-warm notes — a run of signal-orange
 * crash barriers and the blue-white terminal glazing — plus a red-and-blue
 * fuselage. So this list is deliberately half neutrals: roughly one prop in two
 * stays a dirty painted grey and only the rest carries a hue. F15 in FINDINGS
 * is the cost of getting that ratio wrong on the weapon.
 */
const METAL_TINT = [
  [1.00, 0.98, 0.94],   // dirty original
  [0.86, 0.84, 0.80],   // grimed
  [1.12, 1.08, 1.00],   // sun-bleached
  [2.45, 1.05, 0.48],   // signal orange   (the mw3_04 barrier note)
  [2.10, 0.72, 0.52],   // oxide red
  [0.62, 1.05, 1.75],   // faded cobalt
  [0.72, 1.28, 0.86],   // works green
  [2.15, 1.85, 0.78],   // ochre / hazard yellow
];
const WOODISH = new Set(['crate_wood', 'crate_wood_lg', 'pallet', 'produce_crate', 'basket', 'table', 'cardboard']);
const PLASTICISH = new Set(['crate_plastic', 'chair', 'bucket', 'barrel_blue']);
const METALISH = new Set(['barrel', 'jerrycan', 'gas_bottle', 'crate_metal', 'ammo_box']);

/** Deterministic per-instance colour jitter for a prop kind. */
export function variantTint(kind, rng) {
  if (PLASTICISH.has(kind)) {
    const c = PLASTIC_TINT[rng.int(0, PLASTIC_TINT.length - 1)];
    const k = rng.range(0.86, 1.10);
    return [c[0] * k, c[1] * k, c[2] * k];
  }
  if (METALISH.has(kind)) {
    const c = METAL_TINT[rng.int(0, METAL_TINT.length - 1)];
    const k = rng.range(0.80, 1.10);
    return [c[0] * k, c[1] * k, c[2] * k];
  }
  if (WOODISH.has(kind)) {
    const k = rng.range(0.78, 1.16);
    return [k * rng.range(0.96, 1.10), k * rng.range(0.92, 1.04), k * rng.range(0.82, 1.0)];
  }
  const k = rng.range(0.84, 1.14);
  return [k * rng.range(0.97, 1.05), k, k * rng.range(0.94, 1.04)];
}

/** A named accent, for the handful of props placed by hand as colour anchors. */
export function accentTint(name, rng) {
  const map = {
    orange: [2.45, 1.05, 0.48], red: [2.10, 0.72, 0.52], blue: [0.62, 1.05, 1.75],
    green: [0.72, 1.28, 0.86], yellow: [2.15, 1.85, 0.78], white: [1.35, 1.34, 1.30],
  };
  const c = map[name] || map.orange;
  const k = rng ? rng.range(0.88, 1.08) : 1;
  return [c[0] * k, c[1] * k, c[2] * k];
}

/**
 * Props that can be found lying on their side, with the height their centre
 * rests at once tipped. A yard where nothing has ever fallen over reads as a
 * prop library laid out on a grid.
 */
const TIPPED = {
  barrel: { rx: Math.PI / 2, lift: 0.31 },
  barrel_blue: { rx: Math.PI / 2, lift: 0.29 },
  crate_wood: { rz: Math.PI / 2, lift: 0.37 },
  crate_wood_lg: { rz: Math.PI / 2, lift: 0.54 },
  crate_plastic: { rz: Math.PI / 2, lift: 0.31 },
  bucket: { rx: Math.PI / 2, lift: 0.17 },
  jerrycan: { rz: Math.PI / 2, lift: 0.18 },
  gas_bottle: { rx: Math.PI / 2, lift: 0.18 },
  cardboard: { rz: Math.PI / 2, lift: 0.24 },
  basket: { rx: Math.PI / 2, lift: 0.20 },
  produce_crate: { rz: Math.PI / 2, lift: 0.29 },
};

/* ------------------------------------------------------------------ *
 * placement helpers
 * ------------------------------------------------------------------ */

/** Staggered sandbag emplacement between two points. */
export function sandbagWall(w, { a, b, rows = 3, y = 0, jitter = 0.05 }) {
  const rng = w.rng;
  const L = Math.hypot(b[0] - a[0], b[1] - a[1]);
  const rotY = Math.atan2(-(b[1] - a[1]), b[0] - a[0]);
  const per = Math.max(1, Math.round(L / 0.5));
  for (let r = 0; r < rows; r++) {
    const off = (r % 2) * 0.25;
    const n = per - (r % 2);
    for (let i = 0; i < n; i++) {
      const t = (i * 0.5 + off + 0.25) / L;
      if (t > 1) continue;
      const x = a[0] + (b[0] - a[0]) * t;
      const z = a[1] + (b[1] - a[1]) * t;
      const inset = (r * 0.035) * (rng.sign());
      // two bag shapes with different baked UV phase, plus per-bag scale, tilt
      // and hessian tint: the emplacement is no longer one bag stamped 40 times
      const kind = ((i + r) % 3 === 1) ? 'sandbag_b' : 'sandbag';
      const dirt = rng.range(0.80, 1.16);
      w.props.place(kind, xform(
        [x + Math.sin(rotY) * inset, y + 0.19 * r + 0.01 + rng.range(-0.02, 0.02), z + Math.cos(rotY) * inset],
        [rng.range(-0.09, 0.09), rotY + rng.range(-0.22, 0.22), rng.range(-0.07, 0.07)],
        [1 + rng.range(-jitter, jitter) * 2, 1 + rng.range(-0.14, 0.12), 1 + rng.range(-jitter, jitter) * 2]),
      [dirt * rng.range(0.98, 1.08), dirt * rng.range(0.94, 1.02), dirt * rng.range(0.84, 0.98)]);
    }
  }
  w.blocker((a[0] + b[0]) / 2, y + rows * 0.185 / 2, (a[1] + b[1]) / 2, L, rows * 0.185, 0.42, rotY, 'sand');
}

/**
 * Rubble at the base of a damaged wall.
 *
 * One instanced definition serves every pile on the map, so the variation has
 * to come from the placement matrix. Uniform scale + Y-rotation alone leaves a
 * recognisable silhouette repeating across the level ("prop library"); a tilt
 * about X/Z and a non-uniform scale break the repeat for free, and the pile
 * mounds toward its centre instead of lying in a flat ring.
 */
export function rubblePile(w, { x, z, y = 0, radius = 1.4, count = 7, bricks = 5, bar = true }) {
  const rng = w.rng;
  for (let i = 0; i < count; i++) {
    const a = rng.range(0, Math.PI * 2), t = Math.sqrt(rng()), r = t * radius;
    const mound = (1 - t) * 0.16 * rng.range(0.4, 1.4);   // heaped, not scattered
    const v = rng.range(0.82, 1.14);
    w.props.place('rubble_chunk', xform(
      [x + Math.cos(a) * r, y + mound + 0.01, z + Math.sin(a) * r],
      // the tilt is half what it was: +-0.34 rad on a 7 cm slab buried its far
      // corner and left a thin wedge above the floor, which is what read as a
      // "flat card intersecting the floor at a random angle"
      [rng.range(-0.17, 0.17), rng.range(0, 6.28), rng.range(-0.17, 0.17)],
      [rng.range(0.65, 1.4), rng.range(0.6, 1.2), rng.range(0.7, 1.35)]),
    [v * rng.range(0.9, 0.99), v, v * rng.range(1.0, 1.09)]);
  }
  for (let i = 0; i < bricks; i++) {
    const a = rng.range(0, Math.PI * 2), t = Math.sqrt(rng()), r = t * radius * 1.3;
    const v = rng.range(0.74, 1.06);
    w.props.place('brick_debris', xform(
      [x + Math.cos(a) * r, y + (1 - t) * 0.06 * rng() + 0.01, z + Math.sin(a) * r],
      [rng.range(-0.2, 0.2), rng.range(0, 6.28), rng.range(-0.2, 0.2)],
      [rng.range(0.8, 1.3), rng.range(0.8, 1.2), rng.range(0.8, 1.3)]),
    [v * rng.range(0.9, 1.0), v * rng.range(1.0, 1.06), v * rng.range(1.02, 1.12)]);
  }
  if (bar) rebar(w, { x, y: y + 0.02, z, count: rng.int(2, 4), spread: radius * 0.4, len: 0.7 });
  w.blocker(x, y + 0.12, z, radius * 1.4, 0.24, radius * 1.4, 0, 'gravel', false);
}

/** Grass and weeds in the cracks. */
export function weeds(w, { x0, z0, x1, z1, count = 60, y = 0, avoid = [], tries = 6 }) {
  const rng = w.rng;
  const blocked = (x, z) => avoid.some((a) => x > a[0] && x < a[2] && z > a[1] && z < a[3]);
  for (let i = 0; i < count; i++) {
    let x = 0, z = 0, ok = false;
    for (let t = 0; t < tries && !ok; t++) {
      x = rng.range(x0, x1); z = rng.range(z0, z1);
      ok = !blocked(x, z);
    }
    if (!ok) continue;
    const dry = rng.range(0.72, 1.22);
    w.props.place('grass_tuft',
      xform([x, y, z], rng.range(0, 6.28),
        [rng.range(0.65, 1.5), rng.range(0.6, 1.6), rng.range(0.65, 1.5)]),
      [dry * rng.range(0.95, 1.12), dry, dry * rng.range(0.7, 1.0)]);
  }
}

/**
 * Random-but-deterministic scatter of a prop kind inside a rect.
 *
 * Rejected samples are retried rather than dropped. Dropping made the delivered
 * count depend on how much of the rect the `avoid` lanes covered *and* on where
 * the shared RNG stream happened to be, so an unrelated edit elsewhere in the
 * build could silently halve the dressing in the square.
 */
export function scatter(w, kind, { x0, z0, x1, z1, count, y = 0, scale = [0.9, 1.1], avoid = [], tries = 12, tip = 0.16 }) {
  const rng = w.rng;
  const blocked = (x, z) => avoid.some((a) => x > a[0] && x < a[2] && z > a[1] && z < a[3]);
  const T = TIPPED[kind];
  for (let i = 0; i < count; i++) {
    let x = 0, z = 0, ok = false;
    for (let t = 0; t < tries && !ok; t++) {
      x = rng.range(x0, x1); z = rng.range(z0, z1);
      ok = !blocked(x, z);
    }
    if (!ok) continue;
    const s = rng.range(scale[0], scale[1]);
    /*
     * Per-instance rotation, NON-UNIFORM scale and tint. A uniform scale plus a
     * Y rotation leaves the same box with the same texture phase in six places,
     * which is exactly the note on `level_overview`. Non-uniform scale changes
     * the proportion, the tint changes the value, and one prop in six has been
     * knocked over.
     */
    const sv = [s * rng.range(0.88, 1.12), s * rng.range(0.86, 1.14), s * rng.range(0.88, 1.12)];
    const tint = variantTint(kind, rng);
    if (T && rng() < tip) {
      w.props.place(kind, xform(
        [x, y + T.lift * (T.rx ? sv[2] : sv[0]), z],
        [T.rx || 0, rng.range(0, 6.28), T.rz || 0], sv), tint);
    } else {
      w.props.place(kind, xform([x, y, z], rng.range(0, 6.28), sv), tint);
    }
  }
}

/* ------------------------------------------------------------------ *
 * hero vehicles — merged into the static batch (no extra draw calls)
 * ------------------------------------------------------------------ */

/** Materials whose vehicle parts are excluded from the shadow cascades. */
const NO_CAST_MAT = new Set(['glass', 'glass_dirty', 'metal_grate', 'steel_brushed']);

function wheel(w, x, y, z, rotY, steer = 0, r = 0.34) {
  // TorusGeometry rings around +Z, which is already the wheel axle direction.
  // Neither wheel part casts: they sit inside the arch of a body that already
  // casts, so their shadow is invisible and each would cost a draw per cascade.
  const tyre = stripAttrs(new THREE.TorusGeometry(r * 0.72, r * 0.30, 8, 14).toNonIndexed());
  w.batch.add(tyre, 'rubber', { matrix: xform([x, y, z], rotY + steer), surface: 'rubber', uvScale: 0.3, tint: [0.55, 0.55, 0.56], cast: false });
  tyre.dispose();
  const rim = chamferCyl(r * 0.52, 0.14, 10, 0.02);
  rim.rotateX(Math.PI / 2);
  w.batch.add(rim, 'steel_brushed', { matrix: xform([x, y, z], rotY + steer), surface: 'metal', uvScale: 0.25, cast: false });
  rim.dispose();
}

/** A dusty abandoned sedan, built from bevelled primitives. */
export function sedan(w, { x, z, y = 0, rotY = 0, tint = [0.82, 0.83, 0.84], flat = false, doorOpen = false }) {
  const B = (geo, mat, surface, pos, rot = 0, opts = {}) => {
    const m = new THREE.Matrix4();
    const q = new THREE.Quaternion().setFromEuler(new THREE.Euler(rot[0] ?? 0, (rot[1] ?? 0), rot[2] ?? 0, 'YXZ'));
    m.compose(new THREE.Vector3(pos[0], pos[1], pos[2]), q, new THREE.Vector3(1, 1, 1));
    const world = xform([x, y, z], rotY).multiply(m);
    // Glass and the grille never cast: transparent panes have no business in a
    // depth-only shadow pass, and each extra material that casts is one more
    // draw call per cascade.
    w.batch.add(geo, mat, {
      matrix: world, surface, uvScale: opts.uvScale ?? 0.9, tint: opts.tint ?? tint,
      cast: opts.cast ?? !NO_CAST_MAT.has(mat),
    });
    geo.dispose();
  };
  const drop = flat ? -0.09 : 0;
  // body
  B(chamferBox(4.24, 0.52, 1.74, 0.07), 'metal_painted', 'metal', [0, 0.72 + drop, 0]);
  B(chamferBox(4.30, 0.16, 1.66, 0.05), 'metal_painted', 'metal', [0, 0.50 + drop, 0]);
  B(chamferBox(1.44, 0.24, 1.66, 0.06), 'metal_painted', 'metal', [1.30, 1.02 + drop, 0]);   // bonnet
  B(chamferBox(1.02, 0.26, 1.66, 0.06), 'metal_painted', 'metal', [-1.62, 1.02 + drop, 0]);  // boot
  // cabin
  B(chamferBox(2.20, 0.62, 1.58, 0.09), 'metal_painted', 'metal', [-0.20, 1.32 + drop, 0]);
  B(chamferBox(1.86, 0.10, 1.48, 0.05), 'metal_painted', 'metal', [-0.30, 1.63 + drop, 0]);
  // glass
  B(chamferBox(0.06, 0.52, 1.42, 0.02), 'glass_dirty', 'glass', [0.86, 1.34 + drop, 0], [0, 0, -0.42]);
  B(chamferBox(0.06, 0.50, 1.40, 0.02), 'glass_dirty', 'glass', [-1.24, 1.33 + drop, 0], [0, 0, 0.40]);
  for (const s of [-1, 1]) {
    B(chamferBox(1.72, 0.46, 0.05, 0.02), 'glass_dirty', 'glass', [-0.24, 1.34 + drop, s * 0.76]);
    B(chamferBox(0.08, 0.50, 0.09, 0.02), 'metal_painted', 'metal', [0.62, 1.33 + drop, s * 0.77]);
    B(chamferBox(0.08, 0.50, 0.09, 0.02), 'metal_painted', 'metal', [-1.06, 1.33 + drop, s * 0.77]);
    // mirrors, handles, arches
    B(chamferBox(0.20, 0.11, 0.07, 0.02), 'metal_painted', 'metal', [0.72, 1.20 + drop, s * 0.94]);
    B(chamferBox(0.22, 0.05, 0.05, 0.015), 'steel_brushed', 'metal', [0.10, 1.02 + drop, s * 0.88]);
    B(chamferBox(0.22, 0.05, 0.05, 0.015), 'steel_brushed', 'metal', [-0.95, 1.02 + drop, s * 0.88]);
  }
  // bumpers + lights
  B(chamferBox(0.22, 0.30, 1.72, 0.05), 'plastic_crate', 'plastic', [2.05, 0.72 + drop, 0], 0, { tint: [0.42, 0.42, 0.44] });
  B(chamferBox(0.20, 0.28, 1.72, 0.05), 'plastic_crate', 'plastic', [-2.10, 0.74 + drop, 0], 0, { tint: [0.42, 0.42, 0.44] });
  for (const s of [-1, 1]) {
    B(chamferBox(0.10, 0.18, 0.44, 0.03), 'glass', 'glass', [2.06, 0.98 + drop, s * 0.58], 0, { tint: [1.1, 1.08, 1.0] });
    B(chamferBox(0.09, 0.17, 0.36, 0.03), 'glass', 'glass', [-2.10, 1.00 + drop, s * 0.62], 0, { tint: [1.2, 0.5, 0.42] });
  }
  B(chamferBox(0.12, 0.20, 1.02, 0.03), 'metal_grate', 'metal', [2.02, 0.96 + drop, 0]);
  if (doorOpen) {
    B(chamferBox(1.02, 0.86, 0.08, 0.03), 'metal_painted', 'metal', [0.55, 1.02 + drop, 1.32], [0, -0.85, 0]);
  }
  // wheels
  const wr = flat ? 0.26 : 0.34;
  for (const [dx, dz, st] of [[1.35, 0.86, 0.06], [1.35, -0.86, 0.06], [-1.34, 0.86, 0], [-1.34, -0.86, 0]]) {
    const cos = Math.cos(rotY), sin = Math.sin(rotY);
    wheel(w, x + cos * dx + sin * dz, y + wr, z - sin * dx + cos * dz, rotY, st, wr);
  }
  w.blocker(x, y + 0.85, z, 4.5, 1.7, 1.9, rotY, 'metal');
}

/** A knackered pickup truck. */
export function pickup(w, { x, z, y = 0, rotY = 0, tint = [0.78, 0.74, 0.66], rollBar = true }) {
  const B = (geo, mat, surface, pos, rot = 0, opts = {}) => {
    const m = new THREE.Matrix4();
    const q = new THREE.Quaternion().setFromEuler(new THREE.Euler(rot[0] ?? 0, rot[1] ?? 0, rot[2] ?? 0, 'YXZ'));
    m.compose(new THREE.Vector3(pos[0], pos[1], pos[2]), q, new THREE.Vector3(1, 1, 1));
    w.batch.add(geo, mat, {
      matrix: xform([x, y, z], rotY).multiply(m), surface, uvScale: opts.uvScale ?? 0.9,
      tint: opts.tint ?? tint, cast: opts.cast ?? !NO_CAST_MAT.has(mat),
    });
    geo.dispose();
  };
  B(chamferBox(5.05, 0.46, 1.86, 0.06), 'metal_painted', 'metal', [0, 0.86, 0]);
  B(chamferBox(5.10, 0.14, 1.72, 0.05), 'metal_painted', 'metal', [0, 0.62, 0]);
  B(chamferBox(1.62, 0.30, 1.80, 0.06), 'metal_painted', 'metal', [1.62, 1.20, 0]);          // bonnet
  B(chamferBox(1.86, 0.86, 1.80, 0.08), 'metal_painted', 'metal', [0.10, 1.52, 0]);          // cab
  B(chamferBox(1.70, 0.10, 1.70, 0.05), 'metal_painted', 'metal', [0.10, 1.98, 0]);          // roof
  B(chamferBox(0.07, 0.62, 1.58, 0.02), 'glass_dirty', 'glass', [1.02, 1.58, 0], [0, 0, -0.34]);
  B(chamferBox(0.07, 0.58, 1.56, 0.02), 'glass_dirty', 'glass', [-0.80, 1.58, 0], [0, 0, 0.18]);
  for (const s of [-1, 1]) {
    B(chamferBox(1.42, 0.52, 0.05, 0.02), 'glass_dirty', 'glass', [0.14, 1.58, s * 0.88]);
    B(chamferBox(0.24, 0.12, 0.08, 0.02), 'metal_painted', 'metal', [0.98, 1.44, s * 1.02]);
  }
  // bed
  for (const s of [-1, 1]) B(chamferBox(2.28, 0.56, 0.10, 0.03), 'metal_painted', 'metal', [-1.55, 1.36, s * 0.88]);
  B(chamferBox(0.10, 0.56, 1.78, 0.03), 'metal_painted', 'metal', [-2.66, 1.36, 0]);
  B(chamferBox(2.28, 0.06, 1.74, 0.02), 'metal_rusted', 'metal', [-1.55, 1.11, 0], 0, { tint: [0.8, 0.62, 0.5] });
  if (rollBar) {
    for (const s of [-1, 1]) {
      B(chamferBox(0.08, 0.92, 0.08, 0.02), 'metal_rusted', 'metal', [-0.48, 1.86, s * 0.76]);
    }
    B(chamferBox(0.08, 0.08, 1.60, 0.02), 'metal_rusted', 'metal', [-0.48, 2.30, 0]);
    B(chamferBox(0.30, 0.16, 0.30, 0.02), 'metal_painted', 'metal', [-0.48, 2.44, 0.42]);
  }
  B(chamferBox(0.24, 0.32, 1.86, 0.05), 'metal_rusted', 'metal', [2.44, 0.88, 0], 0, { tint: [0.72, 0.6, 0.5] });
  B(chamferBox(0.22, 0.30, 1.86, 0.05), 'metal_rusted', 'metal', [-2.80, 0.90, 0], 0, { tint: [0.72, 0.6, 0.5] });
  for (const s of [-1, 1]) {
    B(chamferBox(0.10, 0.20, 0.40, 0.03), 'glass', 'glass', [2.46, 1.14, s * 0.64], 0, { tint: [1.1, 1.08, 1.0] });
  }
  for (const [dx, dz] of [[1.62, 0.92], [1.62, -0.92], [-1.72, 0.92], [-1.72, -0.92]]) {
    const cos = Math.cos(rotY), sin = Math.sin(rotY);
    wheel(w, x + cos * dx + sin * dz, y + 0.42, z - sin * dx + cos * dz, rotY, 0, 0.42);
  }
  w.blocker(x, y + 1.0, z, 5.3, 2.0, 2.0, rotY, 'metal');
}

/** Hanging rug / fabric on a wall or line — batched so each can be tinted. */
export function hangingFabric(w, { x, y, z, rotY = 0, width = 1.2, height = 1.9, mat = 'canvas', tint = null, rod = true }) {
  /*
   * Three overlapping slabs, not one zero-thickness card.
   *
   * `level_alley` (1400–1800, 285–800) called this "a zero-thickness quad with a
   * razor 90 deg edge", and it was: `quadXY` has no thickness at all, so the rug
   * disappeared edge-on and its silhouette was a hairline. Real thickness plus a
   * slight fold per panel gives it an edge highlight and a shadow of its own.
   */
  const rng = w.rng;
  const panels = 3;
  for (let i = 0; i < panels; i++) {
    const pw = width / panels;
    const g = chamferBox(pw * 1.06, height, 0.032, 0.012);
    const u = (i - (panels - 1) / 2) * pw;
    w.batch.add(g, mat, {
      matrix: xform([x + Math.cos(rotY) * u, y - height / 2 + rng.range(-0.02, 0.02), z - Math.sin(rotY) * u],
        [0, rotY + rng.range(-0.05, 0.05), rng.range(-0.02, 0.02)]),
      surface: 'fabric', uvScale: 0.7, tint,
    });
    g.dispose();
  }
  if (rod) {
    const r = chamferCyl(0.022, width + 0.2, 6, 0.006);
    r.rotateZ(Math.PI / 2);
    w.batch.add(r, 'metal_rusted', { matrix: xform([x, y, z], rotY), surface: 'metal', uvScale: 0.2 });
    r.dispose();
  }
}

/** Cloth-topped market awning stretched between two stalls. */
export function tarpSpan(w, { a, b, width = 2.4, sag = 0.35, tint = null, mat = 'canvas' }) {
  const g = quadXZ(Math.hypot(b[0] - a[0], b[2] - a[2]), width, 6);
  const m = alignX(a, b);
  w.batch.add(g, mat, { matrix: m, surface: 'fabric', uvScale: 0.8, tint });
  g.dispose();
  void sag;
}

export { extrudeShape };
